/**
 * Chart Component
 * Interactive line chart for displaying video analysis data
 */

import { useEffect, useRef, useState } from 'react';
import { max, min } from 'd3-array';
import { scaleBand, scaleLinear } from 'd3-scale';
import { line } from 'd3-shape';
import { timeToSecs } from '../../lib/utils';
import type { ChartProps } from '../../lib/types';

export default function Chart({ data, yLabel, jumpToTimecode }: ChartProps) {
  const chartRef = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(1);
  const [height, setHeight] = useState(1);
  const margin = 55;
  
  if (!data || data.length === 0) {
    return (
      <div style={{ 
        width: '100%', 
        height: '300px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: 'var(--mantine-color-dimmed)'
      }}>
        No chart data available
      </div>
    );
  }
  
  const xMax = width;
  const yMax = height - margin;
  const xScale = scaleBand()
    .range([margin + 10, xMax])
    .domain(data.map((d) => d.time))
    .padding(0.2);

  const vals = data.map((d) => d.value);
  const yScale = scaleLinear()
    .domain([min(vals) || 0, max(vals) || 10])
    .nice()
    .range([yMax, margin]);

  const yTicks = yScale.ticks(Math.floor(height / 70));

  const lineGen = line<{ time: string; value: number }>()
    .x((d) => xScale(d.time) || 0)
    .y((d) => yScale(d.value));

  useEffect(() => {
    const setSize = () => {
      if (chartRef.current) {
        setWidth(chartRef.current.clientWidth);
        setHeight(chartRef.current.clientHeight);
      }
    };

    setSize();
    window.addEventListener('resize', setSize);
    return () => window.removeEventListener('resize', setSize);
  }, []);

  return (
    <svg 
      ref={chartRef}
      style={{ 
        width: '100%', 
        height: '100%',
        minHeight: '300px'
      }}
    >
      {/* Y-axis labels */}
      <g>
        {yTicks.map((tick) => {
          const y = yScale(tick);
          return (
            <g key={tick} transform={`translate(0 ${y})`}>
              <text 
                x={margin - 10} 
                dy="0.25em" 
                textAnchor="end"
                fill="var(--mantine-color-gray-6)"
                fontSize="12px"
              >
                {tick}
              </text>
              <line
                x1={margin - 5}
                x2={xMax}
                stroke="var(--mantine-color-gray-2)"
                strokeWidth="1"
              />
            </g>
          );
        })}
      </g>

      {/* X-axis labels */}
      <g transform={`translate(0 ${yMax + 40})`}>
        {data.map(({ time }, i) => {
          const x = xScale(time) || 0;
          return (
            <text
              key={i}
              x={x}
              textAnchor="middle"
              fill="var(--mantine-color-gray-6)"
              fontSize="12px"
              style={{ cursor: jumpToTimecode ? 'pointer' : 'default' }}
              onClick={() => jumpToTimecode && jumpToTimecode(timeToSecs(time))}
            >
              {time.length > 5 ? time.replace(/^00:/, '') : time}
            </text>
          );
        })}
      </g>

      {/* Chart line */}
      <g>
        <path 
          d={lineGen(data) || ''} 
          fill="none"
          stroke="var(--mantine-color-blue-6)"
          strokeWidth="2"
        />
      </g>

      {/* Data points */}
      <g>
        {data.map(({ time, value }, i) => {
          const x = xScale(time) || 0;
          const y = yScale(value);
          return (
            <g key={i}>
              <circle 
                cx={x} 
                cy={y} 
                r={4}
                fill="var(--mantine-color-blue-6)"
                style={{ cursor: jumpToTimecode ? 'pointer' : 'default' }}
                onClick={() => jumpToTimecode && jumpToTimecode(timeToSecs(time))}
              />
              <text 
                x={x} 
                y={y - 12}
                textAnchor="middle"
                fill="var(--mantine-color-gray-7)"
                fontSize="11px"
                fontWeight="500"
              >
                {value}
              </text>
            </g>
          );
        })}
      </g>

      {/* Y-axis title */}
      {yLabel && (
        <text
          x={margin}
          y={-width + margin}
          transform="rotate(90)"
          textAnchor="middle"
          fill="var(--mantine-color-gray-7)"
          fontSize="12px"
          fontWeight="500"
        >
          {yLabel}
        </text>
      )}
    </svg>
  );
}
